import { apiEndpoints } from '@/apiEndpoints';
import { BenefitType } from '@/types/benefits/benefitTypes';
import { CreateEstimateFormData } from '@/types/estimates/CreateEstimateFormData';
import { EstimateResponse } from '@/types/estimates/EstimateResponse';
import { Estimate } from '@/types/estimates/estimatesColumnDef';
import { IncomingReservation } from '@/types/reservations/IncomingReservationColumnDef';
import { Room } from '@/utils/estimates/findAllRoomCombinations';
import axios from 'axios';
import { UpdateParticipantDto } from './project.api';
import { EstimateInfoResponse } from '@/types/estimates/estimateInfoResponse';
import { EstimateStatus } from '@/types/estimates/EstimateStatus';
import { SupportedLocale } from '@/types/supported-locale.type';
import { HotelPensionType } from '@/types/benefits/hotel/HotelResponse';

export type EstimatesResponse = {
  estimates: Estimate[];
  total: number;
  project: {
    id: string;
    name: string;
    clientId: string;
    clientFirstName?: string | null;
    client: {
      agencyName?: string | null;
      mainContact: {
        firstName: string;
        lastName: string;
      };
    };
    _count: {
      adults: number;
      children: number;
    };
    children: {
      birthDate: string;
    }[];
  };
};

export type EstimateOptions = {
  page?: number;
  pageSize?: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
};

export const getEstimatesForProject = async (
  projectId: string,
  options?: EstimateOptions,
) => {
  const response = await axios.get<EstimatesResponse>(
    apiEndpoints.estimates.byProject(projectId),
    {
      params: options,
    },
  );
  return response.data;
};

export type CreateEstimateDto = {
  circuitId: string;
  marginRate?: number | null;
  name?: string;
  language?: SupportedLocale;
  adults: Omit<CreateEstimateFormData['adults'][number], 'isSelected'>[];
  children: Omit<CreateEstimateFormData['children'][number], 'isSelected'>[];
  editableCircuitId?: string;
  allowAgencyModification?: boolean;
  showQuoteName?: boolean;
  showSummaryAndImportantPoints?: boolean;
  showPriceIncludesExcludes?: boolean;
  showModificationCancellationConditions?: boolean;
  showDates?: boolean;
  allowAgencyInternationalFlights?: boolean;
  allowAgencyMarginRate?: boolean;
  allowAgencyAdditionalMargin?: boolean;
};

export const createEstimate = async (
  createEstimateDto: CreateEstimateDto,
  projectId: string,
  fromEditableCircuit?: boolean,
) => {
  return axios.post(
    apiEndpoints.estimates.byProject(projectId),
    createEstimateDto,
    { params: { fromEditableCircuit } },
  );
};

export const duplicateEstimate = async (
  estimateId: string,
  projectId: string,
) => {
  return axios.post(apiEndpoints.estimates.duplicate(projectId), {
    estimateId,
  });
};

export const deleteEstimate = async (estimateId: string, projectId: string) => {
  return axios.delete(apiEndpoints.estimates.delete(projectId, estimateId));
};

export const getEstimateById = async ({
  estimateId,
  projectId,
}: {
  estimateId: string;
  projectId: string;
}) => {
  const result = await axios.get<EstimateResponse>(
    apiEndpoints.estimates.detail(projectId, estimateId),
  );
  return result.data;
};

type EstimateCarRentalElementDto = {
  elementId: string;
  deltaPrice: number;
  isRequired?: boolean;
};

export type SaveEstimateDto = {
  amount: number;
  daysTitles: string[];
  daysDescriptions: string[];
  summary?: string;
  importantPoints?: string;
  priceIncludes?: string;
  priceExcludes?: string;
  modificationsConditions?: string;
  cancellationConditions?: string;
  arrivalDate: Date;
  customParagraphTitle?: string;
  customParagraphDescription?: string;
  internationalFlightsPrice?: number;
  internationalFlightsNonMarginedTaxes?: number;
  internationalFlightsMarginRate?: number;
  marginRateExistsForInternationalFlights?: boolean;
  agencyMarginRate?: number;
  agencyInternationalFlightsPrice?: number;
  agencyInternationalFlightsNonMarginedTaxes?: number;
  agencyInternationalFlightsMarginRate?: number;
  agencyMarginRateExistsForInternationalFlights?: boolean;
  agencyAdditionalMarginType?: 'FIXED' | 'PERCENT';
  agencyAdditionalMarginValue?: number;
  mainContact: {
    firstName: string;
    lastName: string;
    email?: string;
    phoneNumber?: string;
    address?: string;
    postalCode?: string;
    city?: string;
  };
  options: {
    stepId: string;
    estimateOptionInfo: {
      deltaPrice: number;
      type: BenefitType;
      benefitId: string;
      carCategoryId?: string;
      hotelCategoryId?: string;
      roomCombination?: {
        room: Room;
      }[];
      hotelPensionType?: HotelPensionType;
      carCategoryInsurances?: EstimateCarRentalElementDto[];
      carCategoryOptions?: EstimateCarRentalElementDto[];
      carCategoryTaxes?: EstimateCarRentalElementDto[];
      carCategoryOneWayFees?: EstimateCarRentalElementDto[];
    };
  }[];
  adults?: UpdateParticipantDto[];
  children?: UpdateParticipantDto[];
};

export const saveEstimate = async (
  estimateId: string,
  projectId: string,
  saveEstimateDto: SaveEstimateDto,
) =>
  axios.put(
    apiEndpoints.estimates.save(projectId, estimateId),
    saveEstimateDto,
  );

export const sendReservationRequest = async (
  estimateId: string,
  projectId: string,
  saveEstimateDto: SaveEstimateDto,
) =>
  axios.post(
    apiEndpoints.estimates.reservation(projectId, estimateId),
    saveEstimateDto,
  );

export type ReservationParams = EstimateOptions & {
  filters?: Record<string, string>;
};

export type ReservationRequestsResponse = {
  total: number;
  reservationRequests: IncomingReservation[];
  agents: {
    id: string;
    firstname: string;
    lastname: string;
  }[];
  countries: {
    countries: string[];
  }[];
  agencies: {
    id: string;
    name: string;
  }[];
  statuses: {
    status: EstimateStatus;
  }[];
};

export const getIncomingReservationRequests = async (
  params: ReservationParams,
) => {
  const filtersStr = Object.entries(params.filters ?? {})
    .map(([key, value]) => `${key}:${encodeURIComponent(value)}`)
    .join(',');
  const result = await axios.get<ReservationRequestsResponse>(
    apiEndpoints.estimates.reservationRequests,
    { params: { ...params, filters: filtersStr } },
  );
  return result.data;
};

export const getIncomingReservationRequest = async (estimateId: string) => {
  const result = await axios.get<EstimateResponse>(
    apiEndpoints.estimates.reservationRequestDetail(estimateId),
  );
  return result.data;
};

export const acceptReservationRequest = async (estimateId: string) =>
  axios.post(apiEndpoints.estimates.acceptReservation(estimateId));

export const refuseReservationRequest = async (estimateId: string) =>
  axios.post(apiEndpoints.estimates.refuseReservation(estimateId));

export type EstimateSharedLinkParams = {
  estimateId: string;
  projectId: string;
  accessToken: string;
};

export const getEstimateFromSharedLink = async ({
  accessToken,
  estimateId,
  projectId,
}: EstimateSharedLinkParams) => {
  const result = await axios.get<EstimateResponse>(
    apiEndpoints.estimates.sharedLink(projectId, estimateId),
    {
      params: { accessToken },
    },
  );
  return result.data;
};

export const getEstimateSharedLinkToken = async ({
  estimateId,
}: {
  estimateId: string;
}) => {
  const result = await axios.post<{ token: string }>(
    apiEndpoints.estimates.shareLink(estimateId),
  );
  return result.data;
};

type SaveEstimateAsClientParams = {
  estimateId: string;
  projectId: string;
  saveEstimateDto: SaveEstimateDto;
  accessToken?: string;
};

export const saveEstimateAsClient = async ({
  estimateId,
  projectId,
  saveEstimateDto,
  accessToken,
}: SaveEstimateAsClientParams) =>
  axios.post(
    apiEndpoints.estimates.sharedLinkSave(projectId, estimateId),
    saveEstimateDto,
    { params: { accessToken } },
  );

export const sendReservationRequestAsClient = async ({
  estimateId,
  projectId,
  saveEstimateDto,
  accessToken,
}: SaveEstimateAsClientParams) =>
  axios.post(
    apiEndpoints.estimates.sharedLinkSendReservation(projectId, estimateId),
    saveEstimateDto,
    { params: { accessToken } },
  );

export const revokeSharedLink = async (estimateId: string) =>
  axios.post(apiEndpoints.estimates.sharedLinkRevoke(estimateId));

type ClientLinkParams = {
  estimateId: string;
  projectId: string;
  accessToken?: string;
};

export const createClientSharedLink = async ({
  estimateId,
  projectId,
  accessToken,
}: ClientLinkParams) => {
  const result = await axios.post<{ token: string }>(
    apiEndpoints.estimates.sharedLinkClientLink(projectId, estimateId),
    undefined,
    { params: { accessToken } },
  );
  return result.data;
};

export const revokeClientSharedLink = async ({
  estimateId,
  projectId,
  accessToken,
}: ClientLinkParams) =>
  axios.post(
    apiEndpoints.estimates.sharedLinkClientLinkRevoke(projectId, estimateId),
    undefined,
    { params: { accessToken } },
  );

export type UpdateEstimateInfoDto = Partial<
  Omit<CreateEstimateDto, 'adults' | 'children' | 'circuitId'>
> & {
  adults: UpdateParticipantDto[];
  children: UpdateParticipantDto[];
};

export const updateEstimateInfo = async (
  estimateId: string,
  projectId: string,
  updateEstimateInfoDto: UpdateEstimateInfoDto,
) =>
  axios.patch(
    apiEndpoints.estimates.save(projectId, estimateId),
    updateEstimateInfoDto,
  );

export const getEstimateInfo = async (
  estimateId: string,
  projectId: string,
) => {
  const result = await axios.get<EstimateInfoResponse>(
    apiEndpoints.estimates.info(projectId, estimateId),
  );
  return result.data;
};

export const getAgencyWebsiteFromEstimateId = async (estimateId: string) => {
  const result = await axios.get<{ website: string }>(
    apiEndpoints.companies.agencyWebsite(estimateId),
  );
  return result.data;
};

export const validateEstimateParagraph = async (
  projectId: string,
  estimateId: string,
  field: string,
  value: string,
) =>
  axios.patch(apiEndpoints.estimates.validateParagraph(projectId, estimateId), {
    field,
    value,
  });
